import { Button, Tag, Tooltip, Typography, theme } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { buildNoteCardView, splitHighlight } from "@mappers/notes.mapper.mjs";
import type { VisualizerColumn } from "@mappers/notes.mapper.mjs";
import type { Note, NoteAttribute } from "@store/reducers/notes.reducer";
import { formatNoteDate } from "@utils/notesDates.mjs";
import { ReadersBadge } from "../ReadersBadge";

interface VisualizerNoteProps {
    note: Note;
    column: VisualizerColumn;
    attributes: NoteAttribute[];
    myUuid: string | undefined;
    timeZone: string;
    /** Cut a long note after 4 lines. */
    truncate: boolean;
    /** The pencil shows only when this is set and the note has `can_edit`. */
    onEdit?: (note: Note) => void;
}

export const VisualizerNote = ({ note, column, attributes, myUuid, timeZone, truncate, onEdit }: VisualizerNoteProps) => {
    const { token } = theme.useToken();
    const view = buildNoteCardView(note, attributes, myUuid);
    const { highlight, others } = splitHighlight(view, column.highlightAttributeId);
    const highlightName = attributes.find((a) => a.id === column.highlightAttributeId)?.name;
    return (
        <div className="notes-viz-note">
            {/* The value leads the card, so the values of a column line up for a scan. */}
            {highlight !== null && (
                <Tooltip title={highlightName ? `${highlightName}: ${highlight}` : undefined}>
                    <div className="notes-viz-highlight" style={{ background: token.colorPrimary, color: token.colorWhite }}>
                        {highlight}
                    </div>
                </Tooltip>
            )}
            <div className="notes-viz-note-main">
                {/* A long note shows 4 lines. "Show more" opens the rest in place, as on the note cards. */}
                <Typography.Paragraph
                    className="notes-viz-note-body"
                    ellipsis={truncate && { rows: 4, expandable: "collapsible", symbol: (expanded) => (expanded ? "Show less" : "Show more") }}
                >
                    {note.body}
                </Typography.Paragraph>
                <div className="notes-viz-note-foot">
                    <Typography.Text type="secondary">
                        {view.authorLabel} · {formatNoteDate(note.created_at, timeZone)}
                    </Typography.Text>
                    <span className="notes-viz-note-actions">
                        {view.readers.length > 0 && <ReadersBadge readers={view.readers} />}
                        {others.length > 0 && (
                            <Tooltip
                                title={others.map((a) => (
                                    <div key={a.id}>
                                        {a.name}: {a.value}
                                    </div>
                                ))}
                            >
                                <Tag>+{others.length}</Tag>
                            </Tooltip>
                        )}
                        {onEdit && note.can_edit && (
                            <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                aria-label="Edit note"
                                title="Edit note"
                                data-attr="notes.visualizer.edit-note"
                                onClick={() => onEdit(note)}
                            />
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
};
